import React, { useState } from 'react';
import Table from 'Components/Table';
import { useLocation } from 'react-router-dom';
import { ListEntryFilterResponseObj } from '@brighterion/types/listEntry';
import useGetListEntryTableColumns from './useGetListEntryTableColumns';
import DeleteListEntryModal from '../DeleteListEntryModal';
import EditListEntryModal from '../EditListEntryModal';

type Props = {
    data: ListEntryFilterResponseObj['result'];
    listName: string;
    listId: number;
    listType: SingleListType;
    merchantSpecific: boolean;
    isLoading: boolean;
    customListType?: string;
};

const ListEntryTable = ({ listName, listType, listId, merchantSpecific, data, isLoading, customListType }: Props) => {
    const location = useLocation();
    const { pathname } = location;
    // use ROUTES.LIST_MANAGEMENT.REMOVED_LIST_ENTRIES from src/components/Breadcrumbs
    // list-management/:id/removed-list-entries
    // to identify whether the user is on view removed list entries page
    // if so, hide button
    const isInViewRemovedEntriesPage = pathname.includes('removed-list-entries');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedListEntryId, setSelectedListEntryId] = useState(-1);
    const [rangeListData, setRangeListData] = useState({
        from: -1,
        to: -1,
        merchantId: ''
    });

    const updateModal = React.useCallback(
        (row: UpdateModalFunctionSchema['row'], action: UpdateModalFunctionSchema['action']) => {
            setSelectedListEntryId(row.original.id);
            setIsEditModalOpen(action === 'edit');
            setIsDeleteModalOpen(action === 'delete');

            if (row?.range) {
                setRangeListData({ ...row?.range[0], merchantId: row.original.merchantId });
            }
        },
        []
    );

    const columns = useGetListEntryTableColumns({
        listType,
        merchantSpecific,
        updateModal,
        isInViewRemovedEntriesPage
    });

    return (
        <div>
            <Table
                isLoading={isLoading}
                showNoDataMsg={false}
                dataTestId={`LIST_ENTRY_TABLE_${listName}`}
                columns={columns}
                data={data}
            />
            {isEditModalOpen && (
                <EditListEntryModal
                    isOpen={isEditModalOpen}
                    merchantSpecific={merchantSpecific}
                    onClose={() => setIsEditModalOpen(false)}
                    listEntryId={selectedListEntryId}
                    listId={listId}
                    listEntries={data}
                    listType={listType}
                    customListType={customListType}
                />
            )}
            {isDeleteModalOpen && (
                <DeleteListEntryModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    listEntryId={selectedListEntryId}
                    listId={listId}
                    listName={listName}
                    rowData={rangeListData}
                />
            )}
        </div>
    );
};

export default React.memo(ListEntryTable);
